/**
 * External Layers Action Component
 * Combined component for WMS, DTN Weather, and Vector Tile layers
 * with folder-like navigation structure
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Layers, ChevronRight, ChevronLeft, Cloud, Globe, Grid3X3, Check, Search, Circle } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  Input,
} from '@ais-ui/ui-core';
import { useLayers } from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';
import {
  useDTNToken,
  useDTNCatalog,
  useDTNLayerTimestamps,
  buildDTNTileUrlTemplate,
  getProxiedDTNLayerLegend,
  DTNAuthConfig,
  DTNLayer,
} from '@ais-ui/dtn-weather-api';
import { useSaveSettings, VectorTile, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { useSettings } from '../../../Settings/SettingsProvider';
import {
  ToggleVectorTileInSettings,
  useVectorTileSettings,
} from '../../../VectorTiles/useVectorTileSettings';
import {
  useWMSSettings,
  useWmsLayerActions,
} from '../../../WMSLayers/useWMSSettings';
import { cn } from '@ais-ui/utils';

// Types for navigation
type LayerSourceType = 'wms' | 'dtn' | 'vectortile';

interface NavigationState {
  sourceType: LayerSourceType | null;
  groupId: string | null;
  breadcrumbs: { id: string | null; label: string; sourceType?: LayerSourceType }[];
}

// Get DTN config from environment
function useDTNConfig(): DTNAuthConfig | null {
  const clientId = import.meta.env.VITE_DTN_CLIENT_ID;
  const clientSecret = import.meta.env.VITE_DTN_CLIENT_SECRET;
  if (!clientId || !clientSecret) return null;
  return { clientId, clientSecret };
}

export const ExternalLayers: React.FC = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { 
    addLayer, 
    removeLayer, 
    getLayerById, 
    doesLayerExist, 
    addVectorTile,
  } = useLayers();
  const { mutate: mutateSettings } = useSaveSettings();

  // Navigation state - start at root (showing all groups)
  const [navigation, setNavigation] = useState<NavigationState>({
    sourceType: null,
    groupId: null,
    breadcrumbs: [{ id: null, label: 'External Layers' }],
  });

  // Reset navigation and search when popover closes
  useEffect(() => {
    if (!open) {
      setNavigation({
        sourceType: null,
        groupId: null,
        breadcrumbs: [{ id: null, label: 'External Layers' }],
      });
      setSearchQuery('');
    }
  }, [open]);

  // ==================== WMS ====================
  const wmsSettings = useWMSSettings();
  const { toggleWmsLayer, doesLayerExist: wmsDoesLayerExist } = useWmsLayerActions();

  // ==================== Vector Tiles ====================
  const vectorTiles = useVectorTileSettings();

  // ==================== DTN Weather ====================
  const dtnConfig = useDTNConfig();
  const { data: dtnToken } = useDTNToken(dtnConfig);
  const { data: dtnCatalog } = useDTNCatalog(dtnToken, 'en-US');
  const [selectedDtnLayerId, setSelectedDtnLayerId] = useState<string | null>(null);
  const pendingDtnLayerRef = useRef<DTNLayer | null>(null);
  const { data: dtnTimestamps } = useDTNLayerTimestamps(dtnToken, selectedDtnLayerId || undefined);

  // Filter DTN layers to marine and weather-related
  const filteredDtnLayers = useMemo(() => {
    const allLayers = dtnCatalog?.layers || [];
    const allowedGroups = ['marine', 'observed-radar', 'forecast-radar', 'satellite', 'severe-weather'];
    return allLayers.filter(layer => layer.group && allowedGroups.includes(layer.group));
  }, [dtnCatalog]);

  const filteredDtnGroups = useMemo(() => {
    const allGroups = dtnCatalog?.groups || [];
    const allowedGroupIds = ['marine', 'radar', 'observed-radar', 'forecast-radar', 'satellite', 'severe-weather'];
    return allGroups.filter(group => allowedGroupIds.includes(group.id));
  }, [dtnCatalog]);

  // ==================== Active Layer Tracking ====================
  
  // Check if any WMS layer in a service is active
  const getWmsServiceActiveCount = useCallback((wmsInfo: WMSInfo) => {
    return wmsInfo.layers?.filter(layer => doesLayerExist(layer.id)).length || 0;
  }, [doesLayerExist]);

  // Check if any DTN layer in a group (including subgroups) is active
  const getDtnGroupActiveCount = useCallback((groupId: string) => {
    const directLayers = filteredDtnLayers.filter(l => l.group === groupId);
    const subgroups = filteredDtnGroups.filter(g => g.parent === groupId);
    
    let count = directLayers.filter(l => doesLayerExist(`dtn-weather-${l.id}`)).length;
    
    // Recursively count subgroup layers
    subgroups.forEach(sg => {
      const subgroupLayers = filteredDtnLayers.filter(l => l.group === sg.id);
      count += subgroupLayers.filter(l => doesLayerExist(`dtn-weather-${l.id}`)).length;
    });
    
    return count;
  }, [filteredDtnLayers, filteredDtnGroups, doesLayerExist]);

  // Check if any vector tile is active
  const getVectorTileActiveCount = useCallback(() => {
    return vectorTiles.filter(vt => doesLayerExist(vt.id)).length;
  }, [vectorTiles, doesLayerExist]);

  // Handle DTN timestamp loading and layer creation
  const createDtnTileLayer = useCallback(
    async (layer: DTNLayer, tileSetId: string) => {
      const layerId = `dtn-weather-${layer.id}`;
      try {
        // Check legend availability before adding layer
        let hasLegend = false;
        try {
          const legend = await getProxiedDTNLayerLegend(layer.id, undefined, 'en-US');
          hasLegend = legend && legend.length > 0;
        } catch {
          // Legend not available for this layer
          hasLegend = false;
        }

        // Proxy handles authentication
        const urlTemplate = buildDTNTileUrlTemplate(layer.id, tileSetId, 256);
        const source = new XYZ({
          url: urlTemplate,
          crossOrigin: 'anonymous',
          attributions: '© DTN',
        });
        const tileLayer = new TileLayer({ source, opacity: 0.7 });
        addLayer(
          tileLayer, layer.label, true, false, false,
          { type: 'dtn-weather', dtnLayerId: layer.id, hasLegend },
          layerId, false, false, false, 'above-ships'
        );
        toast({
          title: messages.genericToastTitleSuccess,
          description: `Added weather layer: ${layer.label}`,
        });
      } catch (error) {
        toast({
          title: messages.genericToastTitleError,
          description: `Failed to add weather layer: ${layer.label}`,
          variant: 'destructive',
        });
      }
    },
    [addLayer, toast, messages]
  );

  useEffect(() => {
    if (dtnTimestamps && dtnTimestamps.length > 0 && pendingDtnLayerRef.current) {
      const latestWrapper = dtnTimestamps[dtnTimestamps.length - 1];
      const latestTimestamp = latestWrapper?.timestamps?.[latestWrapper.timestamps.length - 1];
      
      if (latestTimestamp?.tileSetId) {
        createDtnTileLayer(pendingDtnLayerRef.current, latestTimestamp.tileSetId);
        pendingDtnLayerRef.current = null;
        setSelectedDtnLayerId(null);
      }
    }
  }, [dtnTimestamps, createDtnTileLayer]);

  // ==================== Layer Selection Handlers ====================

  const handleWmsLayerSelect = useCallback(
    async (wmsInfo: WMSInfo, wmsLayerInfo: WMSLayerInfo) => {
      await toggleWmsLayer(wmsInfo, wmsLayerInfo);
    },
    [toggleWmsLayer]
  );

  const handleVectorTileSelect = useCallback(
    (vectorTile: VectorTile) => {
      const enable = !doesLayerExist(vectorTile.id);
      const settingsToSave = ToggleVectorTileInSettings(vectorTiles, vectorTile.id, enable, settings);

      if (enable) {
        addVectorTile(vectorTile, true, vectorTile.id);
      } else {
        const layerItem = getLayerById(vectorTile.id);
        if (layerItem) removeLayer(layerItem.layer);
      }

      mutateSettings(settingsToSave, {
        onSuccess: () => {
          toast({
            title: messages.genericToastTitleSuccess,
            description: enable
              ? `Added vector tile: ${vectorTile.name}`
              : `Removed vector tile: ${vectorTile.name}`,
          });
        },
        onError: () => {
          toast({
            title: messages.genericToastTitleError,
            description: `Failed to ${enable ? 'add' : 'remove'} vector tile: ${vectorTile.name}`,
            variant: 'destructive',
          });
        },
      });
    },
    [doesLayerExist, vectorTiles, settings, addVectorTile, getLayerById, removeLayer, mutateSettings, toast, messages]
  );

  const handleDtnLayerSelect = useCallback(
    (layer: DTNLayer) => {
      const layerId = `dtn-weather-${layer.id}`;
      if (doesLayerExist(layerId)) {
        const existingLayer = getLayerById(layerId);
        if (existingLayer) {
          removeLayer(existingLayer.layer);
          toast({
            title: messages.genericToastTitleSuccess,
            description: `Removed weather layer: ${layer.label}`,
          });
        }
      } else {
        pendingDtnLayerRef.current = layer;
        setSelectedDtnLayerId(layer.id);
      }
    },
    [doesLayerExist, getLayerById, removeLayer, toast, messages]
  );

  // ==================== Navigation ====================

  const navigateToGroup = useCallback((
    groupId: string, 
    label: string, 
    sourceType: LayerSourceType
  ) => {
    setNavigation(prev => ({
      sourceType,
      groupId,
      breadcrumbs: [...prev.breadcrumbs, { id: groupId, label, sourceType }],
    }));
    setSearchQuery('');
  }, []);

  const navigateBack = useCallback(() => {
    setNavigation(prev => {
      if (prev.breadcrumbs.length <= 1) return prev;
      const newBreadcrumbs = prev.breadcrumbs.slice(0, -1);
      const lastCrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
      
      if (newBreadcrumbs.length === 1) {
        return { sourceType: null, groupId: null, breadcrumbs: newBreadcrumbs };
      }
      return { 
        sourceType: lastCrumb.sourceType || null, 
        groupId: lastCrumb.id, 
        breadcrumbs: newBreadcrumbs 
      };
    });
    setSearchQuery('');
  }, []);

  // ==================== Search & Filter ====================

  const normalizedQuery = searchQuery.toLowerCase().trim();

  // Filter WMS services and layers by search
  const filteredWmsServices = useMemo(() => {
    if (!wmsSettings?.wmsInfos) return [];
    if (!normalizedQuery) return wmsSettings.wmsInfos;
    
    return wmsSettings.wmsInfos.map(wms => ({
      ...wms,
      layers: wms.layers?.filter(layer => 
        layer.title.toLowerCase().includes(normalizedQuery) ||
        wms.name.toLowerCase().includes(normalizedQuery)
      )
    })).filter(wms => 
      wms.name.toLowerCase().includes(normalizedQuery) ||
      (wms.layers && wms.layers.length > 0)
    );
  }, [wmsSettings?.wmsInfos, normalizedQuery]);

  // Filter DTN groups and layers by search
  const filteredSearchDtnGroups = useMemo(() => {
    if (!normalizedQuery) return filteredDtnGroups;
    return filteredDtnGroups.filter(group =>
      group.label.toLowerCase().includes(normalizedQuery)
    );
  }, [filteredDtnGroups, normalizedQuery]);

  const filteredSearchDtnLayers = useMemo(() => {
    if (!normalizedQuery) return filteredDtnLayers;
    return filteredDtnLayers.filter(layer =>
      layer.label.toLowerCase().includes(normalizedQuery)
    );
  }, [filteredDtnLayers, normalizedQuery]);

  // Filter vector tiles by search
  const filteredSearchVectorTiles = useMemo(() => {
    if (!vectorTiles) return [];
    if (!normalizedQuery) return vectorTiles;
    return vectorTiles.filter(vt =>
      vt.name.toLowerCase().includes(normalizedQuery)
    );
  }, [vectorTiles, normalizedQuery]);

  // ==================== Render Helpers ====================

  const hasWmsLayers = wmsSettings?.wmsInfos && wmsSettings.wmsInfos.length > 0;
  const hasVectorTiles = vectorTiles && vectorTiles.length > 0;
  const hasDtnLayers = dtnConfig && filteredDtnLayers.length > 0;

  // Root level - show all groups directly (WMS services, DTN top-level groups, Vector Tiles)
  const renderRootGroups = () => {
    const items: React.ReactNode[] = [];

    // When searching, show matching layers directly
    if (normalizedQuery) {
      // Show matching WMS layers directly
      filteredWmsServices.forEach(wmsInfo => {
        wmsInfo.layers?.forEach(layer => {
          items.push(
            <LayerItem
              key={`wms-${layer.id}`}
              label={layer.title}
              sublabel={wmsInfo.name}
              isActive={doesLayerExist(layer.id)}
              isLoading={false}
              onClick={() => handleWmsLayerSelect(wmsInfo, layer)}
            />
          );
        });
      });

      // Show matching DTN layers directly
      filteredSearchDtnLayers.forEach(layer => {
        const group = filteredDtnGroups.find(g => g.id === layer.group);
        items.push(
          <LayerItem
            key={`dtn-${layer.id}`}
            label={layer.label}
            sublabel={group?.label}
            isActive={doesLayerExist(`dtn-weather-${layer.id}`)}
            isLoading={selectedDtnLayerId === layer.id}
            onClick={() => handleDtnLayerSelect(layer)}
          />
        );
      });

      // Show matching vector tiles directly
      filteredSearchVectorTiles.forEach(vt => {
        items.push(
          <LayerItem
            key={`vt-${vt.id}`}
            label={vt.name}
            sublabel="Vector Tile"
            isActive={doesLayerExist(vt.id)}
            isLoading={false}
            onClick={() => handleVectorTileSelect(vt)}
          />
        );
      });

      if (items.length === 0) {
        return (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers found
          </div>
        );
      }

      return <div className="flex flex-col gap-1">{items}</div>;
    }

    // Normal view - show groups
    // WMS Services
    if (hasWmsLayers) {
      wmsSettings.wmsInfos.forEach(wmsInfo => {
        const activeCount = getWmsServiceActiveCount(wmsInfo);
        items.push(
          <GroupItem
            key={`wms-${wmsInfo.id}`}
            icon={<Globe className="size-4" />}
            label={wmsInfo.name}
            count={wmsInfo.layers?.length || 0}
            activeCount={activeCount}
            onClick={() => navigateToGroup(wmsInfo.id, wmsInfo.name, 'wms')}
          />
        );
      });
    }

    // DTN Weather Groups (top-level only)
    if (hasDtnLayers) {
      const topLevelGroups = filteredDtnGroups.filter(g => !g.parent);
      topLevelGroups.forEach(group => {
        const subgroups = filteredDtnGroups.filter(g => g.parent === group.id);
        const directLayers = filteredDtnLayers.filter(l => l.group === group.id);
        const subgroupLayers = subgroups.flatMap(sg => 
          filteredDtnLayers.filter(l => l.group === sg.id)
        );
        const totalCount = directLayers.length + subgroupLayers.length;
        const activeCount = getDtnGroupActiveCount(group.id);
        
        items.push(
          <GroupItem
            key={`dtn-${group.id}`}
            icon={<Cloud className="size-4" />}
            label={group.label}
            count={totalCount}
            activeCount={activeCount}
            onClick={() => navigateToGroup(group.id, group.label, 'dtn')}
          />
        );
      });
    }

    // Vector Tiles as a single group
    if (hasVectorTiles) {
      const activeCount = getVectorTileActiveCount();
      items.push(
        <GroupItem
          key="vectortiles"
          icon={<Grid3X3 className="size-4" />}
          label="Vector Tiles"
          count={vectorTiles.length}
          activeCount={activeCount}
          onClick={() => navigateToGroup('vectortiles', 'Vector Tiles', 'vectortile')}
        />
      );
    }

    if (items.length === 0) {
      return (
        <div className="text-sm text-muted-foreground text-center py-4">
          No external layers configured
        </div>
      );
    }

    return <div className="flex flex-col gap-1">{items}</div>;
  };

  // WMS layers within a service
  const renderWmsLayers = () => {
    const wmsInfo = wmsSettings.wmsInfos.find(w => w.id === navigation.groupId);
    if (!wmsInfo) return null;
    
    const layers = normalizedQuery 
      ? wmsInfo.layers?.filter(l => l.title.toLowerCase().includes(normalizedQuery))
      : wmsInfo.layers;
    
    return (
      <div className="flex flex-col gap-1">
        {layers?.sort((a, b) => a.title.localeCompare(b.title)).map(layer => (
          <LayerItem
            key={layer.id}
            label={layer.title}
            isActive={doesLayerExist(layer.id)}
            isLoading={false}
            onClick={() => handleWmsLayerSelect(wmsInfo, layer)}
          />
        ))}
        {(!layers || layers.length === 0) && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers
          </div>
        )}
      </div>
    );
  };

  // DTN subgroups or layers within a group
  const renderDtnGroupContent = () => {
    const subgroups = filteredDtnGroups.filter(g => g.parent === navigation.groupId);
    let directLayers = filteredDtnLayers.filter(l => l.group === navigation.groupId);

    // Apply search filter
    const filteredSubgroups = normalizedQuery
      ? subgroups.filter(sg => sg.label.toLowerCase().includes(normalizedQuery))
      : subgroups;
    
    if (normalizedQuery) {
      directLayers = directLayers.filter(l => l.label.toLowerCase().includes(normalizedQuery));
    }

    return (
      <div className="flex flex-col gap-1">
        {/* Subgroups */}
        {filteredSubgroups.map(subgroup => {
          const layerCount = filteredDtnLayers.filter(l => l.group === subgroup.id).length;
          const activeCount = getDtnGroupActiveCount(subgroup.id);
          return (
            <GroupItem
              key={subgroup.id}
              icon={<Cloud className="size-4" />}
              label={subgroup.label}
              count={layerCount}
              activeCount={activeCount}
              onClick={() => navigateToGroup(subgroup.id, subgroup.label, 'dtn')}
            />
          );
        })}
        {/* Direct layers */}
        {directLayers.sort((a, b) => a.label.localeCompare(b.label)).map(layer => (
          <LayerItem
            key={layer.id}
            label={layer.label}
            isActive={doesLayerExist(`dtn-weather-${layer.id}`)}
            isLoading={selectedDtnLayerId === layer.id}
            onClick={() => handleDtnLayerSelect(layer)}
          />
        ))}
        {filteredSubgroups.length === 0 && directLayers.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers
          </div>
        )}
      </div>
    );
  };

  // Vector tiles (flat list)
  const renderVectorTiles = () => {
    const filtered = normalizedQuery
      ? vectorTiles.filter(vt => vt.name.toLowerCase().includes(normalizedQuery))
      : vectorTiles;

    return (
      <div className="flex flex-col gap-1">
        {filtered.map(vt => (
          <LayerItem
            key={vt.id}
            label={vt.name}
            isActive={doesLayerExist(vt.id)}
            isLoading={false}
            onClick={() => handleVectorTileSelect(vt)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers
          </div>
        )}
      </div>
    );
  };

  // Determine what to render based on navigation state
  const renderContent = () => {
    // Root level - show all groups
    if (!navigation.groupId) {
      return renderRootGroups();
    }

    // Inside a WMS service
    if (navigation.sourceType === 'wms') {
      return renderWmsLayers();
    }

    // Inside a DTN group
    if (navigation.sourceType === 'dtn') {
      return renderDtnGroupContent();
    }

    // Vector tiles
    if (navigation.sourceType === 'vectortile') {
      return renderVectorTiles();
    }

    return null;
  };

  const currentTitle = navigation.breadcrumbs[navigation.breadcrumbs.length - 1]?.label || 'External Layers';
  const canGoBack = navigation.breadcrumbs.length > 1;

  // Calculate total active layers count
  const totalActiveCount = useMemo(() => {
    // Count active WMS layers
    const wmsCount = (wmsSettings?.wmsInfos || []).reduce((acc: number, wms: WMSInfo) => acc + getWmsServiceActiveCount(wms), 0);
    
    // Count active DTN layers
    const dtnCount = filteredDtnLayers.filter(l => doesLayerExist(`dtn-weather-${l.id}`)).length;
    
    // Count active Vector Tiles
    const vectorCount = getVectorTileActiveCount();
    
    return wmsCount + dtnCount + vectorCount;
  }, [wmsSettings?.wmsInfos, getWmsServiceActiveCount, filteredDtnLayers, doesLayerExist, getVectorTileActiveCount]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="external-layers-button"
              size="icon"
              variant="outline"
              className="h-10 w-10 flex-shrink-0 flex-grow-0 relative"
            >
              <Layers className="size-5" />
              {totalActiveCount > 0 && (
                <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full">
                  {totalActiveCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>External Layers</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80 p-0">
        {/* Header with back button and title */}
        <div className="flex items-center gap-2 p-3 border-b">
          {canGoBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={navigateBack}
            >
              <ChevronLeft className="size-4" />
            </Button>
          )}
          <span className="font-medium text-sm flex-1 truncate">{currentTitle}</span>
        </div>

        {/* Search */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search layers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </div>
        
        {/* Content */}
        <ScrollArea className="h-72">
          <div className="p-2">
            {renderContent()}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

// ==================== Sub-components ====================

interface GroupItemProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  activeCount: number;
  onClick: () => void;
}

const GroupItem: React.FC<GroupItemProps> = ({ icon, label, count, activeCount, onClick }) => (
  <button
    className={cn(
      'flex items-center gap-3 w-full px-3 py-2 rounded-md text-left',
      'hover:bg-accent hover:text-accent-foreground transition-colors'
    )}
    onClick={onClick}
  >
    <span className="text-muted-foreground">{icon}</span>
    <span className="flex-1 text-sm font-medium truncate">{label}</span>
    {activeCount > 0 && (
      <span className="flex items-center gap-1">
        <Circle className="size-2 fill-primary text-primary" />
        <span className="text-xs text-primary font-medium">{activeCount}</span>
      </span>
    )}
    <span className="text-xs text-muted-foreground">{count}</span>
    <ChevronRight className="size-4 text-muted-foreground" />
  </button>
);

interface LayerItemProps {
  label: string;
  sublabel?: string;
  isActive: boolean;
  isLoading: boolean;
  onClick: () => void;
}

const LayerItem: React.FC<LayerItemProps> = ({ label, sublabel, isActive, isLoading, onClick }) => (
  <button
    className={cn(
      'flex items-center gap-3 w-full px-3 py-2 rounded-md text-left',
      'hover:bg-accent hover:text-accent-foreground transition-colors',
      isActive && 'bg-accent/50'
    )}
    onClick={onClick}
    disabled={isLoading}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm truncate">
        {isLoading ? 'Loading...' : label}
      </div>
      {sublabel && (
        <div className="text-xs text-muted-foreground truncate">{sublabel}</div>
      )}
    </div>
    {isActive && <Check className="size-4 text-primary shrink-0" />}
  </button>
);

export default ExternalLayers;
